import { useAtom } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import { useEffect } from "react";
import { axios } from "../../../api/axios";
import { Toaster } from "react-hot-toast";
import { User } from "../../../models/user.model";
import { ResponseWithMessage } from "../../../types/ResponseWithMessage";
import { config } from "../../../config/config";
import { WithAuthProps } from "./with-auth.props";
import { Spin } from "antd";
import { styled } from "styled-components";
import { LoadingOutlined } from "@ant-design/icons";
import { colors } from "../../../theme/colors";
import { Theme, themeAtom } from "../../../atoms/theme.atom";
import { store } from "../../../atoms";
import { MdHelp } from "react-icons/md";
import { helpAtom } from "../../../atoms/help.atom";
import { CreateTicket } from "../create-ticket/create-ticket.component";
import { Spacer } from "../../ui/spacer/spacer.component";

export const WithAuth = ({
  children,
  reverse = false,
  showHelp = true,
}: WithAuthProps) => {
  const [auth, setAuth] = useAtom(authAtom);

  const [{ showHelpModal }, setHelp] = useAtom(helpAtom);

  // Load authentication state if not loaded
  useEffect(() => {
    if (!auth.loaded) {
      axios
        .get<ResponseWithMessage<{ user: User }>>("/auth/user/me")
        .then((response) => {
          setAuth({
            user: response.data.user,
            authenticated: true,
            loaded: true,
          });
          store.set(themeAtom, {
            mode: response.data.user.dark_mode ? Theme.Dark : Theme.Light,
          });
          const setTheme = +(localStorage.getItem("theme_mode") ?? 0);
          const newTheme = response.data.user.dark_mode ? 0 : 1;

          if (setTheme !== newTheme) {
            localStorage.setItem("theme_mode", String(newTheme));
            window.location.reload();
          }
        })
        .catch(() => {
          setAuth({
            user: null,
            authenticated: false,
            loaded: true,
          });
        });
    }
  }, [auth]);

  // Listen to authentication state and redirect to auth page if not set successfully
  useEffect(() => {
    console.log(auth.loaded, auth.authenticated, reverse);
    if (auth.loaded && auth.authenticated === reverse) {
      window.location.replace(`${config.auth_url_source}&r=${config.base_url}`);
    }
  }, [auth]);

  return (
    <>
      <CreateTicket
        open={showHelpModal}
        setOpen={(open) => setHelp({ showHelpModal: open })}
        onCreated={() => setHelp({ showHelpModal: false, created: true })}
      />
      {showHelp ? (
        <TicketButton onClick={() => setHelp({ showHelpModal: true })}>
          <MdHelp
            size={30}
            color={colors.primary.hex}
            style={{ transform: "rotate(90deg)" }}
          />
          <Spacer direction="bottom" size={10} />
          Get Help
          <Spacer direction="bottom" size={10} />
        </TicketButton>
      ) : null}
      <Toaster
        toastOptions={{
          style: {
            fontSize: "1.3rem",
          },
        }}
      />
      {auth.loaded && auth.authenticated ? (
        children
      ) : (
        <Main>
          <Spinner
            indicator={
              <LoadingOutlined
                color={colors.primary.hex}
                style={{ fontSize: 48 }}
                spin
              />
            }
          />
        </Main>
      )}
    </>
  );
};

const Main = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${colors.lightBackground.hex};
`;

const Spinner = styled(Spin)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const TicketButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: ${colors.complement.hex};
  border-radius: 0 0.5rem 0.5rem 0;
  position: fixed;
  bottom: 20vh;
  right: 0;
  padding: 1.5rem 1.2rem;
  z-index: 10;
  font-size: 1.5rem;
  font-weight: 600;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
`;
